// Globals
import { TFunction } from 'react-i18next';
import { ColumnsType } from 'antd/es/table';

// Models
import { ROUTES } from '@/models/router.model';
import { IOrderProduct } from '@/models/orders.model';

// Components
import { TrashIcon } from '@/icons';
import {
  StyledActionButton,
  StyledLink,
  StyledNameWrapper,
  StyledCustomOrderBadge,
  StyledDeletedText,
} from './OrderProductsListStyles';
import { InputNumber } from '@/ui';
import { FormItem } from '@/components';

// Helpers
import { OrderDetailsFormFieldsNames, MeasureUnitCode } from '../../helpers';

interface IGetOrderProductsColumnsProps {
  t: TFunction;
  handleDelete: (record: IOrderProduct) => void;
  productsValidationRules: {
    required: boolean;
    message: string;
  }[];
  isEditable: boolean;
}

export const getOrdersTableColumns = ({
  t,
  handleDelete,
  productsValidationRules,
  isEditable,
}: IGetOrderProductsColumnsProps): ColumnsType<IOrderProduct> => {
  return [
    {
      title: t('order_products_list_name'),
      dataIndex: 'name',
      className: 'name-column',
      key: 'name',
      render: (_, record) => (
        <StyledNameWrapper>
          {record?.deleted_at ? (
            <>{record.name}</>
          ) : (
            <StyledLink href={`${ROUTES.PRODUCTS}/${record.id}`} target="_blank" rel="noreferrer">
              {record.name}
            </StyledLink>
          )}
          {record?.is_custom_order && (
            <StyledCustomOrderBadge>{t('order_products_list_is_custom_order_label')}</StyledCustomOrderBadge>
          )}
          {record?.deleted_at && <StyledDeletedText>{t('order_products_deleted')}</StyledDeletedText>}
        </StyledNameWrapper>
      ),
    },
    {
      title: t('order_products_list_quantity'),
      className: 'quantity-column',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (_, record) => {
        return isEditable ? (
          <FormItem
            key={record.id}
            name={[OrderDetailsFormFieldsNames.PRODUCTS, record.id]}
            rules={productsValidationRules}
          >
            <InputNumber />
          </FormItem>
        ) : (
          record.quantity
        );
      },
    },
    {
      title: t('order_products_list_total_quantity'),
      className: 'quantity-column',
      dataIndex: 'stock_quantity',
      key: 'stock_quantity',
      render: (stock_quantity) => stock_quantity ?? 0,
    },
    {
      title: t('order_products_list_total_stock'),
      className: 'quantity-column',
      dataIndex: 'total_stock_quantity',
      key: 'total_stock_quantity',
      render: (total_stock_quantity) => total_stock_quantity ?? 0,
    },
    {
      title: t('order_products_list_partner_article'),
      dataIndex: 'external_id',
      key: 'external_id',
      render: (external_id: string | null | undefined) => external_id ?? '—',
    },
    {
      title: t('order_products_list_sku'),
      dataIndex: 'sku',
      key: 'sku',
      render: (sku) => sku,
    },
    {
      title: t('order_products_list_category'),
      dataIndex: 'category',
      key: 'category',
      render: (_, record) =>
        record.category_id && !record.category_deleted_at ? (
          <StyledLink href={`${ROUTES.CATEGORIES}/${record.category_id}`} target="_blank" rel="noreferrer">
            {record.category}
          </StyledLink>
        ) : (
          record.category
        ),
    },
    {
      title: t('order_products_list_price'),
      dataIndex: 'price',
      key: 'price',
      render: (_, record) =>
        record.price +
        ' ₴ / ' +
        t(`product_unit_short_name_${record.unit_code_snapshot || MeasureUnitCode.PIECE}`, { ns: 'products' }),
    },
    {
      title: t('order_products_list_package_quantity'),
      dataIndex: 'package_price',
      key: 'package_price',
      render: (_, record) =>
        Number(record?.package_quantity_snapshot) +
        ' ' +
        t(`product_unit_short_name_${record.unit_code_snapshot || MeasureUnitCode.PIECE}`, { ns: 'products' }),
    },
    {
      title: t('order_products_list_total_price'),
      dataIndex: 'total_price',
      key: 'total_price',
      render: (total_price) => total_price,
    },
    ...(isEditable
      ? [
          {
            title: t('order_products_list_actions'),
            key: 'actions',
            render: (_: unknown, record: IOrderProduct) => (
              <StyledActionButton onClick={() => handleDelete(record)}>
                <TrashIcon />
              </StyledActionButton>
            ),
          },
        ]
      : []),
  ];
};
